import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';

import DocumentViewer from 'src/components/documents/document-viewer';
import HighlightedText from 'src/components/highlight-text/highlight-text';

// ----------------------------------------------------------------------

/**
 * Display-only tidy of the parsed resume_text: joins a bullet marker the parser left stranded on its
 * own line onto the text that follows, and collapses runs of blank lines. Purely cosmetic — the
 * stored text is never modified.
 */
const tidyResumeText = (text: string): string =>
  text
    .replace(/^[ \t]*([•·‣▪])[ \t]*\r?\n+[ \t]*(?=\S)/gm, '$1 ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

interface Props {
  resumeText?: string | null;
  resumeDocUrl?: string | null;
  /** Search terms to highlight in the parsed text (forces the text view so hits are visible). */
  highlightTerms?: string[];
  /**
   * Which view to show first when both a document and parsed text exist. The parsed-text view is the
   * only one that can highlight, so a highlight term always forces text. Default `document` preserves
   * the uploaded CV's original formatting (record panel); the profile Resume tab passes `text`.
   */
  prefer?: 'text' | 'document';
}

/**
 * Shared resume body (spec items 14/18; JT1-1529 / JT1-1537). Renders the parsed `resume_text` (with
 * optional search-term highlighting) or the uploaded document in a PDF viewer, falling back to an
 * empty message. Reused by the record-panel quick view and the candidate profile Resume tab so both
 * render resumes identically.
 */
export default function CandidateResumeView({ resumeText, resumeDocUrl, highlightTerms, prefer = 'document' }: Props) {
  const { t } = useTranslate();

  const terms = highlightTerms ?? [];
  const hasText = Boolean(resumeText);
  const showText = hasText && (prefer === 'text' || terms.length > 0 || !resumeDocUrl);

  if (showText) {
    return (
      <Box
        sx={{
          whiteSpace: 'pre-wrap',
          typography: 'body2',
          lineHeight: 1.8,
          color: 'text.primary',
          p: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        <HighlightedText text={tidyResumeText(resumeText ?? '')} terms={terms} />
      </Box>
    );
  }

  if (resumeDocUrl) {
    // minHeight gives the PDF viewer a definite height on auto-height parents (the profile Resume tab)
    // while height:100% still lets it fill the flex record-panel body.
    return (
      <Box sx={{ height: '100%', minHeight: '70vh' }}>
        <DocumentViewer fileUrl={resumeDocUrl} />
      </Box>
    );
  }

  return (
    <Typography sx={{ py: 5 }} align="center" variant="body2">
      {t('candidates.drawer.no_resume')}
    </Typography>
  );
}
